import { AnimatePresence, motion } from '@/lib/motion';
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Globe,
  Loader2,
  Phone,
  PhoneIncoming,
  Sparkles,
  User,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
  {
    id: 'create',
    label: 'Create your agent',
    description: 'Name your agent, pick a purpose, and set the tone in seconds.',
    icon: Sparkles,
  },
  {
    id: 'knowledge',
    label: 'Add your knowledge',
    description: 'Paste your website URL — the agent crawls your content and learns your business.',
    icon: BookOpen,
  },
  {
    id: 'number',
    label: 'Connect a phone number',
    description: 'Pick a local number. Calls to that number go straight to your agent.',
    icon: Phone,
  },
  {
    id: 'live',
    label: "You're live",
    description: 'Your agent answers every call, 24/7 — same knowledge, same brand voice.',
    icon: Zap,
  },
] as const;

type StepId = (typeof STEPS)[number]['id'];

const STEP_DURATION = 4500;

// ─── Component ───────────────────────────────────────────────────────────────

export function AgentSetupWalkthrough(): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % STEPS.length);
    }, STEP_DURATION);
  };

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const goTo = (i: number) => {
    setActiveIndex(i);
    resetTimer();
  };

  const step = STEPS[activeIndex];

  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
          Setup walkthrough
        </span>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          Live in under 10 minutes.
        </h2>
        <p className="mt-4 text-muted-foreground">
          Four steps from sign-up to a working AI receptionist answering real calls.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
        {/* Step list */}
        <div className="space-y-3">
          {STEPS.map((s, i) => {
            const active = i === activeIndex;
            const done = i < activeIndex;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => goTo(i)}
                className={`group w-full rounded-2xl border p-5 text-left transition-all duration-300 ${
                  active
                    ? 'border-[var(--brand-accent)]/60 bg-[var(--brand-accent)]/5 shadow-md shadow-[var(--brand-accent)]/10'
                    : 'border-border bg-card hover:border-[var(--brand-accent)]/30 hover:bg-muted/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                      done
                        ? 'bg-emerald-500 text-white'
                        : active
                          ? 'bg-[var(--brand-accent)] text-white shadow-lg shadow-[var(--brand-accent)]/30'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-sm font-semibold transition-colors ${
                          active ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                        }`}
                      >
                        {s.label}
                      </p>
                      <span className="text-xs text-muted-foreground">
                        Step {i + 1}
                      </span>
                    </div>
                    <AnimatePresence initial={false}>
                      {active && (
                        <motion.p
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden text-sm text-muted-foreground mt-1"
                        >
                          {s.description}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 flex-shrink-0 mt-0.5 transition-all ${
                      active ? 'text-[var(--brand-accent)] rotate-90' : 'text-muted-foreground/40'
                    }`}
                  />
                </div>

                {/* Progress bar */}
                {active && (
                  <div className="mt-4 h-0.5 overflow-hidden rounded-full bg-[var(--brand-accent)]/20">
                    <motion.div
                      className="h-full bg-[var(--brand-accent)]"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: STEP_DURATION / 1000, ease: 'linear' }}
                      key={`bar-${activeIndex}`}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Animated UI preview */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-[var(--brand-accent)]/15 to-emerald-500/10 blur-2xl" />
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            {/* Browser chrome */}
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2.5">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/60" />
              </div>
              <div className="rounded-md border border-border bg-background/50 px-3 py-1 text-xs text-muted-foreground">
                app · agents / new
              </div>
              <span className="w-12" />
            </div>

            <div className="relative min-h-[360px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step?.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0"
                >
                  <StepPanel stepId={step?.id || 'create'} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Individual step panels ───────────────────────────────────────────────────

function StepPanel({ stepId }: { stepId: StepId }): JSX.Element {
  switch (stepId) {
    case 'create':
      return <CreatePanel />;
    case 'knowledge':
      return <KnowledgePanel />;
    case 'number':
      return <NumberPanel />;
    case 'live':
      return <LivePanel />;
    default:
      return <CreatePanel />;
  }
}

// Step 1 — Create agent
function CreatePanel(): JSX.Element {
  const [nameVal, setNameVal] = useState('');
  const fullName = 'Reception AI';

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i++;
      setNameVal(fullName.slice(0, i));
      if (i >= fullName.length) clearInterval(t);
    }, 80);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">New agent</p>
        <h3 className="mt-1 text-base font-semibold">Configure your receptionist</h3>
      </div>

      <FakeField label="Agent name">
        <span className="text-foreground">{nameVal}</span>
        <motion.span
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="inline-block w-0.5 h-4 bg-foreground align-middle ml-0.5"
        />
      </FakeField>

      <FakeField label="Purpose">
        <span className="text-foreground">General receptionist</span>
        <ChevronRight className="ml-auto h-3.5 w-3.5 rotate-90 text-muted-foreground" />
      </FakeField>

      <FakeField label="Greeting">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-muted-foreground text-xs leading-relaxed"
        >
          Hi, thanks for calling! I&apos;m your virtual receptionist. How can I help you today?
        </motion.span>
      </FakeField>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="flex justify-end"
      >
        <FakeButton>Save &amp; continue</FakeButton>
      </motion.div>
    </div>
  );
}

// Step 2 — Knowledge base
function KnowledgePanel(): JSX.Element {
  const [phase, setPhase] = useState<'input' | 'crawling' | 'done'>('input');
  const pages = ['Home', 'About', 'Services', 'FAQ', 'Contact'];

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('crawling'), 900);
    const t2 = setTimeout(() => setPhase('done'), 3200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="p-6 space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">Knowledge base</p>
        <h3 className="mt-1 text-base font-semibold">Train from your website</h3>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm">
        <Globe className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">https://</span>
        <span className="text-foreground">yourbusiness.com.au</span>
      </div>

      <AnimatePresence mode="wait">
        {phase === 'input' && (
          <motion.div key="btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex justify-end">
            <FakeButton>Start crawl</FakeButton>
          </motion.div>
        )}
        {phase === 'crawling' && (
          <motion.div key="crawl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Discovering pages…
            </div>
            {pages.slice(0, 3).map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.3 }}
                className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-xs"
              >
                <Loader2 className="h-3 w-3 animate-spin text-[var(--brand-accent)]" />
                <span className="text-muted-foreground">{p}</span>
              </motion.div>
            ))}
          </motion.div>
        )}
        {phase === 'done' && (
          <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
              5 pages indexed · knowledge base ready
            </div>
            {pages.map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-2 rounded-md bg-emerald-500/5 border border-emerald-500/20 px-3 py-1.5 text-xs"
              >
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span>{p}</span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Step 3 — Phone number
const NUMBERS = [
  { num: '(415) 555-0132', region: 'San Francisco' },
  { num: '(212) 555-0147', region: 'New York' },
  { num: '(512) 555-0189', region: 'Austin' },
];

function NumberPanel(): JSX.Element {
  const [selected, setSelected] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setSelected(0), 800);
    const t2 = setTimeout(() => setConnected(true), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="p-6 space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">Phone number</p>
        <h3 className="mt-1 text-base font-semibold">Pick a local number</h3>
      </div>

      <div className="space-y-2">
        {NUMBERS.map((n, i) => (
          <motion.button
            key={n.num}
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15 }}
            onClick={() => setSelected(i)}
            className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition-all ${
              selected === i
                ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/5'
                : 'border-border bg-muted/20 hover:border-[var(--brand-accent)]/40'
            }`}
          >
            <div>
              <p className="font-medium">{n.num}</p>
              <p className="text-xs text-muted-foreground">{n.region}</p>
            </div>
            {selected === i && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="h-4 w-4 rounded-full bg-[var(--brand-accent)]"
              />
            )}
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {connected && selected !== null && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 text-sm"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <span>
              <span className="font-medium">{NUMBERS[selected]?.num}</span>
              <span className="text-muted-foreground"> connected to your agent</span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Step 4 — Live
function LivePanel(): JSX.Element {
  const [phase, setPhase] = useState<'ringing' | 'active' | 'summary'>('ringing');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('active'), 1400);
    const t2 = setTimeout(() => setPhase('summary'), 3200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="p-6 space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-500">Live</p>
        <h3 className="mt-1 text-base font-semibold">Incoming call</h3>
      </div>

      <AnimatePresence mode="wait">
        {phase === 'ringing' && (
          <motion.div key="ring" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-4 py-6">
            <motion.div
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30"
            >
              <PhoneIncoming className="h-7 w-7 text-emerald-500" />
            </motion.div>
            <div className="text-center">
              <p className="font-medium">+61 4XX XXX XXX</p>
              <p className="text-sm text-muted-foreground">Incoming call · ringing…</p>
            </div>
            <div className="flex gap-3">
              <div className="h-9 w-20 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center">
                <span className="text-xs text-red-500">Decline</span>
              </div>
              <motion.div
                animate={{ boxShadow: ['0 0 0 0 rgba(34,197,94,0.4)', '0 0 0 12px rgba(34,197,94,0)', '0 0 0 0 rgba(34,197,94,0)'] }}
                transition={{ duration: 1.2, repeat: Infinity }}
                className="h-9 w-20 rounded-full bg-emerald-500 flex items-center justify-center"
              >
                <span className="text-xs text-white font-medium">Answer</span>
              </motion.div>
            </div>
          </motion.div>
        )}

        {phase === 'active' && (
          <motion.div key="active" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-emerald-500">
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="h-2 w-2 rounded-full bg-emerald-500"
              />
              Live call · 0:12
            </div>
            <FakeChatBubble role="caller" text="Hi, what are your opening hours?" delay={0} />
            <FakeChatBubble
              role="agent"
              text="We're open Monday to Friday, 8am to 6pm AEST. Would you like to make a booking?"
              delay={0.5}
            />
          </motion.div>
        )}

        {phase === 'summary' && (
          <motion.div key="summary" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              Call ended · 1m 24s
            </div>
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">AI Summary</p>
              <p className="text-sm">Caller asked about opening hours. Provided hours and offered booking. Caller declined — will call back.</p>
              <div className="flex gap-2 pt-1">
                <span className="rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs px-2.5 py-0.5">Helpful</span>
                <span className="rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs px-2.5 py-0.5">No action needed</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function FakeField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex min-h-[36px] items-center gap-1 rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm">
        {children}
      </div>
    </div>
  );
}

function FakeButton({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-lg bg-[var(--brand-accent)] px-4 py-2 text-sm font-medium text-white">
      {children}
    </div>
  );
}

function FakeChatBubble({
  role,
  text,
  delay,
}: {
  role: 'caller' | 'agent';
  text: string;
  delay: number;
}): JSX.Element {
  const isAgent = role === 'agent';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`flex items-start gap-2 ${isAgent ? '' : 'flex-row-reverse'}`}
    >
      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-white text-xs ${
          isAgent ? 'bg-[var(--brand-accent)]' : 'bg-muted-foreground/40'
        }`}
      >
        {isAgent ? <Zap className="h-3 w-3" /> : <User className="h-3 w-3" />}
      </div>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
          isAgent
            ? 'bg-[var(--brand-accent)]/10 text-foreground rounded-tl-sm'
            : 'bg-muted text-foreground rounded-tr-sm'
        }`}
      >
        {text}
      </div>
    </motion.div>
  );
}
